import FileIcon from '~/assets/icons/File';
import FileImage from '~/assets/icons/FileImage';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { IFile } from '~/services/modules/contract/types';
import { fileToBase64 } from '~/utils/fileToBase64';
import { radius } from '~/styles/tokens/shape';
import ContractSerialNumberHelp from './ContractSerialNumberHelp';

type ContractJourneyFileUploadProps = {
  appId: string;
  label: string;
  error?: boolean;
  showSerialHelp?: boolean;
  onChangeFile: (file: IFile) => void;
};

export default function ContractJourneyFileUpload({
  appId,
  label,
  error,
  showSerialHelp,
  onChangeFile
}: ContractJourneyFileUploadProps) {
  const [existFile, setExistFile] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (files) => {
      if (!files[0]) return;
      setExistFile(true);
      if (files[0].type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(files[0]));
      } else {
        setPreviewUrl(null);
      }
      const res = await fileToBase64(files[0]);
      const filePayload = res?.split(',')[1];
      onChangeFile({
        Name: files[0].name,
        File: filePayload as string
      });
    },
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    }
  });

  return (
    <Box data-app-id={appId} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, justifyContent: 'center', px: 2 }}>
        <Typography color="primary.main" data-app-id={`${appId}-label`} textAlign="center" variant="h6">
          {label}
        </Typography>
        {showSerialHelp ? <ContractSerialNumberHelp appId={`${appId}-serial-help`} /> : null}
      </Box>
      <Box
        {...getRootProps()}
        data-app-id={`${appId}-dropzone`}
        sx={{
          alignItems: 'center',
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: `${radius.large}px`,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          justifyContent: 'center',
          minHeight: 150,
          p: 2
        }}
      >
        <input {...getInputProps()} data-app-id={`${appId}-input`} />
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt=""
            sx={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Box sx={{ color: 'text.secondary' }}>{existFile ? <FileImage /> : <FileIcon />}</Box>
        )}
        <Typography align="center" color="text.primary" variant="caption">
          {existFile ? 'Clique aqui para substituir o arquivo' : 'Clique aqui para fazer o carregamento do arquivo'}
        </Typography>
      </Box>
    </Box>
  );
}
